/*
 * QR Code generator — vendored, dependency-free port of Nayuki's public-domain
 * "QR Code generator library" (https://www.nayuki.io/page/qr-code-generator-library).
 *
 * We vendor rather than add an npm dependency (per the repo's "prefer a tiny
 * pure-JS/SVG implementation over heavy deps" rule). Trimmed to what the WhatsApp
 * Onboarding QR (#189) needs: byte-mode text encoding + a boolean module readout
 * an SVG can draw from. The encoding algorithm (segment sizing, Reed–Solomon ECC,
 * data interleaving, the 8 mask patterns + penalty scoring) is faithful to the
 * reference, so the output is a standards-conformant, scannable QR symbol.
 *
 * Public domain. No warranty.
 *
 * Usage:
 *   import { encodeText, Ecc } from './qrcodegen';
 *   const qr = encodeText('https://wa.me/1555…', Ecc.MEDIUM);
 *   qr.size;                 // module count per side (21..177)
 *   qr.getModule(x, y);      // true = dark module
 */

// Error-correction level. `ordinal` indexes the ECC tables; `formatBits` is the
// 2-bit code baked into the format information.
export const Ecc = {
    LOW: { ordinal: 0, formatBits: 1 },
    MEDIUM: { ordinal: 1, formatBits: 0 },
    QUARTILE: { ordinal: 2, formatBits: 3 },
    HIGH: { ordinal: 3, formatBits: 2 },
};

const MIN_VERSION = 1;
const MAX_VERSION = 40;

// ── ECC tables (indexed [ecl.ordinal][version]; version 0 unused) ─────
const ECC_CODEWORDS_PER_BLOCK = [
    // 1   2   3   4   5   6   7   8   9  10  11  12  13  14  15  16  17  18  19  20  21  22  23  24  25  26  27  28  29  30  31  32  33  34  35  36  37  38  39  40
    [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Low
    [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], // Medium
    [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Quartile
    [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // High
];
const NUM_ERROR_CORRECTION_BLOCKS = [
    // 1   2   3   4   5   6   7   8   9  10  11  12  13  14  15  16  17  18  19  20  21  22  23  24  25  26  27  28  29  30  31  32  33  34  35  36  37  38  39  40
    [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25], // Low
    [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49], // Medium
    [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68], // Quartile
    [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81], // High
];

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

// ── Byte-mode segment ────────────────────────────────────────────────
function utf8Bytes(str) {
    // TextEncoder is available in every browser/jsdom we run in.
    return Array.from(new TextEncoder().encode(str));
}

function makeBytesSegment(data) {
    const bits = [];
    for (const b of data) {
        for (let i = 7; i >= 0; i--) bits.push((b >>> i) & 1);
    }
    return { mode: 'byte', numChars: data.length, bitData: bits };
}

// Byte mode character-count-indicator bit width by version group.
function charCountBits(version) {
    if (version >= 1 && version <= 9) return 8;
    if (version >= 10 && version <= 26) return 16;
    return 16;
}

function getTotalBits(seg, version) {
    // 4 (mode indicator) + count-indicator + payload.
    return 4 + charCountBits(version) + seg.bitData.length;
}

// ── GF(2^8) Reed–Solomon ─────────────────────────────────────────────
function reedSolomonMultiply(x, y) {
    let z = 0;
    for (let i = 7; i >= 0; i--) {
        z = (z << 1) ^ ((z >>> 7) * 0x11d);
        z ^= ((y >>> i) & 1) * x;
    }
    return z & 0xff;
}

function reedSolomonComputeDivisor(degree) {
    const result = new Array(degree).fill(0);
    result[degree - 1] = 1;
    let root = 1;
    for (let i = 0; i < degree; i++) {
        for (let j = 0; j < result.length; j++) {
            result[j] = reedSolomonMultiply(result[j], root);
            if (j + 1 < result.length) result[j] ^= result[j + 1];
        }
        root = reedSolomonMultiply(root, 0x02);
    }
    return result;
}

function reedSolomonComputeRemainder(data, divisor) {
    const result = new Array(divisor.length).fill(0);
    for (const b of data) {
        const factor = b ^ result.shift();
        result.push(0);
        divisor.forEach((coef, i) => {
            result[i] ^= reedSolomonMultiply(coef, factor);
        });
    }
    return result;
}

// ── QrCode ───────────────────────────────────────────────────────────
class QrCode {
    constructor(version, ecl, dataCodewords, msk) {
        this.version = version;
        this.errorCorrectionLevel = ecl;
        this.size = version * 4 + 17;
        this.modules = [];
        this.isFunction = [];
        for (let i = 0; i < this.size; i++) {
            this.modules.push(new Array(this.size).fill(false));
            this.isFunction.push(new Array(this.size).fill(false));
        }
        this.drawFunctionPatterns();
        const allCodewords = this.addEccAndInterleave(dataCodewords);
        this.drawCodewords(allCodewords);

        // Choose the mask that minimises the penalty (msk < 0 = auto).
        let mask = msk;
        if (mask === -1) {
            let minPenalty = Infinity;
            for (let i = 0; i < 8; i++) {
                this.applyMask(i);
                this.drawFormatBits(i);
                const penalty = this.getPenaltyScore();
                if (penalty < minPenalty) { mask = i; minPenalty = penalty; }
                this.applyMask(i); // undo (XOR is its own inverse)
            }
        }
        this.mask = mask;
        this.applyMask(mask);
        this.drawFormatBits(mask);
        this.isFunction = [];
    }

    getModule(x, y) {
        return x >= 0 && x < this.size && y >= 0 && y < this.size && this.modules[y][x];
    }

    setFunctionModule(x, y, isDark) {
        this.modules[y][x] = isDark;
        this.isFunction[y][x] = true;
    }

    drawFunctionPatterns() {
        // Timing patterns.
        for (let i = 0; i < this.size; i++) {
            this.setFunctionModule(6, i, i % 2 === 0);
            this.setFunctionModule(i, 6, i % 2 === 0);
        }
        // Finder patterns (with separators).
        this.drawFinderPattern(3, 3);
        this.drawFinderPattern(this.size - 4, 3);
        this.drawFinderPattern(3, this.size - 4);
        // Alignment patterns.
        const alignPatPos = this.getAlignmentPatternPositions();
        const numAlign = alignPatPos.length;
        for (let i = 0; i < numAlign; i++) {
            for (let j = 0; j < numAlign; j++) {
                if (!((i === 0 && j === 0) || (i === 0 && j === numAlign - 1) || (i === numAlign - 1 && j === 0))) {
                    this.drawAlignmentPattern(alignPatPos[i], alignPatPos[j]);
                }
            }
        }
        // Format + version info (dummy mask 0 for now — real one drawn later).
        this.drawFormatBits(0);
        this.drawVersion();
    }

    drawFormatBits(mask) {
        const data = (this.errorCorrectionLevel.formatBits << 3) | mask;
        let rem = data;
        for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
        const bits = ((data << 10) | rem) ^ 0x5412;
        // First copy.
        for (let i = 0; i <= 5; i++) this.setFunctionModule(8, i, ((bits >>> i) & 1) !== 0);
        this.setFunctionModule(8, 7, ((bits >>> 6) & 1) !== 0);
        this.setFunctionModule(8, 8, ((bits >>> 7) & 1) !== 0);
        this.setFunctionModule(7, 8, ((bits >>> 8) & 1) !== 0);
        for (let i = 9; i < 15; i++) this.setFunctionModule(14 - i, 8, ((bits >>> i) & 1) !== 0);
        // Second copy.
        for (let i = 0; i < 8; i++) this.setFunctionModule(this.size - 1 - i, 8, ((bits >>> i) & 1) !== 0);
        for (let i = 8; i < 15; i++) this.setFunctionModule(8, this.size - 15 + i, ((bits >>> i) & 1) !== 0);
        this.setFunctionModule(8, this.size - 8, true); // always-dark module
    }

    drawVersion() {
        if (this.version < 7) return;
        let rem = this.version;
        for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
        const bits = (this.version << 12) | rem;
        for (let i = 0; i < 18; i++) {
            const bit = ((bits >>> i) & 1) !== 0;
            const a = this.size - 11 + (i % 3);
            const b = Math.floor(i / 3);
            this.setFunctionModule(a, b, bit);
            this.setFunctionModule(b, a, bit);
        }
    }

    drawFinderPattern(x, y) {
        for (let dy = -4; dy <= 4; dy++) {
            for (let dx = -4; dx <= 4; dx++) {
                const dist = Math.max(Math.abs(dx), Math.abs(dy));
                const xx = x + dx;
                const yy = y + dy;
                if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) {
                    this.setFunctionModule(xx, yy, dist !== 2 && dist !== 4);
                }
            }
        }
    }

    drawAlignmentPattern(x, y) {
        for (let dy = -2; dy <= 2; dy++) {
            for (let dx = -2; dx <= 2; dx++) {
                this.setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
            }
        }
    }

    getAlignmentPatternPositions() {
        if (this.version === 1) return [];
        const numAlign = Math.floor(this.version / 7) + 2;
        const step = (this.version === 32) ? 26
            : Math.ceil((this.version * 4 + 4) / (numAlign * 2 - 2)) * 2;
        const result = [6];
        for (let pos = this.size - 7; result.length < numAlign; pos -= step) {
            result.splice(1, 0, pos);
        }
        return result;
    }

    // ── ECC / interleave ──────────────────────────────────────────────
    addEccAndInterleave(data) {
        const ver = this.version;
        const ecl = this.errorCorrectionLevel;
        const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecl.ordinal][ver];
        const blockEccLen = ECC_CODEWORDS_PER_BLOCK[ecl.ordinal][ver];
        const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
        const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
        const shortBlockLen = Math.floor(rawCodewords / numBlocks);

        const blocks = [];
        const rsDiv = reedSolomonComputeDivisor(blockEccLen);
        for (let i = 0, k = 0; i < numBlocks; i++) {
            const datLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
            const dat = data.slice(k, k + datLen);
            k += datLen;
            const ecc = reedSolomonComputeRemainder(dat, rsDiv);
            if (i < numShortBlocks) dat.push(0);
            blocks.push(dat.concat(ecc));
        }

        const result = [];
        for (let i = 0; i < blocks[0].length; i++) {
            blocks.forEach((block, j) => {
                // Omit the padding cell added to short data blocks.
                if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
                    result.push(block[i]);
                }
            });
        }
        return result;
    }

    drawCodewords(data) {
        let i = 0; // bit index into `data`
        for (let right = this.size - 1; right >= 1; right -= 2) {
            if (right === 6) right = 5;
            for (let vert = 0; vert < this.size; vert++) {
                for (let j = 0; j < 2; j++) {
                    const x = right - j;
                    const upward = ((right + 1) & 2) === 0;
                    const y = upward ? this.size - 1 - vert : vert;
                    if (!this.isFunction[y][x] && i < data.length * 8) {
                        this.modules[y][x] = ((data[i >>> 3] >>> (7 - (i & 7))) & 1) !== 0;
                        i++;
                    }
                }
            }
        }
    }

    applyMask(mask) {
        for (let y = 0; y < this.size; y++) {
            for (let x = 0; x < this.size; x++) {
                let invert;
                switch (mask) {
                    case 0: invert = (x + y) % 2 === 0; break;
                    case 1: invert = y % 2 === 0; break;
                    case 2: invert = x % 3 === 0; break;
                    case 3: invert = (x + y) % 3 === 0; break;
                    case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
                    case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
                    case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
                    case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
                    default: throw new Error('bad mask');
                }
                if (!this.isFunction[y][x] && invert) this.modules[y][x] = !this.modules[y][x];
            }
        }
    }

    getPenaltyScore() {
        let result = 0;
        const size = this.size;
        const modules = this.modules;

        // Adjacent modules in rows / columns.
        for (let y = 0; y < size; y++) {
            let runColor = false;
            let runX = 0;
            const runHistory = [0, 0, 0, 0, 0, 0, 0];
            for (let x = 0; x < size; x++) {
                if (modules[y][x] === runColor) {
                    runX++;
                    if (runX === 5) result += PENALTY_N1;
                    else if (runX > 5) result++;
                } else {
                    this.finderPenaltyAddHistory(runX, runHistory);
                    if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
                    runColor = modules[y][x];
                    runX = 1;
                }
            }
            result += this.finderPenaltyTerminateAndCount(runColor, runX, runHistory) * PENALTY_N3;
        }
        for (let x = 0; x < size; x++) {
            let runColor = false;
            let runY = 0;
            const runHistory = [0, 0, 0, 0, 0, 0, 0];
            for (let y = 0; y < size; y++) {
                if (modules[y][x] === runColor) {
                    runY++;
                    if (runY === 5) result += PENALTY_N1;
                    else if (runY > 5) result++;
                } else {
                    this.finderPenaltyAddHistory(runY, runHistory);
                    if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
                    runColor = modules[y][x];
                    runY = 1;
                }
            }
            result += this.finderPenaltyTerminateAndCount(runColor, runY, runHistory) * PENALTY_N3;
        }

        // 2×2 blocks of the same colour.
        for (let y = 0; y < size - 1; y++) {
            for (let x = 0; x < size - 1; x++) {
                const color = modules[y][x];
                if (color === modules[y][x + 1] && color === modules[y + 1][x] && color === modules[y + 1][x + 1]) {
                    result += PENALTY_N2;
                }
            }
        }

        // Balance of dark vs light modules.
        let dark = 0;
        for (const row of modules) dark += row.reduce((a, b) => a + (b ? 1 : 0), 0);
        const total = size * size;
        const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
        result += k * PENALTY_N4;
        return result;
    }

    finderPenaltyCountPatterns(runHistory) {
        const n = runHistory[1];
        const core = n > 0 && runHistory[2] === n && runHistory[3] === n * 3 && runHistory[4] === n && runHistory[5] === n;
        return (core && runHistory[0] >= n * 4 && runHistory[6] >= n ? 1 : 0)
            + (core && runHistory[6] >= n * 4 && runHistory[0] >= n ? 1 : 0);
    }

    finderPenaltyTerminateAndCount(currentRunColor, currentRunLength, runHistory) {
        let runLen = currentRunLength;
        if (currentRunColor) {
            this.finderPenaltyAddHistory(runLen, runHistory);
            runLen = 0;
        }
        runLen += this.size; // add light border to final run
        this.finderPenaltyAddHistory(runLen, runHistory);
        return this.finderPenaltyCountPatterns(runHistory);
    }

    finderPenaltyAddHistory(currentRunLength, runHistory) {
        let runLen = currentRunLength;
        if (runHistory[0] === 0) runLen += this.size; // add light border to initial run
        runHistory.pop();
        runHistory.unshift(runLen);
    }
}

// Number of data + ecc modules (before subtracting function-pattern modules).
function getNumRawDataModules(ver) {
    let result = (16 * ver + 128) * ver + 64;
    if (ver >= 2) {
        const numAlign = Math.floor(ver / 7) + 2;
        result -= (25 * numAlign - 10) * numAlign - 55;
        if (ver >= 7) result -= 36;
    }
    return result;
}

function getNumDataCodewords(ver, ecl) {
    return Math.floor(getNumRawDataModules(ver) / 8)
        - ECC_CODEWORDS_PER_BLOCK[ecl.ordinal][ver] * NUM_ERROR_CORRECTION_BLOCKS[ecl.ordinal][ver];
}

// ── Public: encode a text string in byte mode ────────────────────────
export function encodeText(text, ecl = Ecc.MEDIUM) {
    const seg = makeBytesSegment(utf8Bytes(String(text)));

    // Find the smallest version that holds the segment at this ECC level.
    let version = MIN_VERSION;
    for (; ; version++) {
        if (version > MAX_VERSION) throw new Error('Data too long for a QR code');
        const dataCapacityBits = getNumDataCodewords(version, ecl) * 8;
        const usedBits = getTotalBits(seg, version);
        if (usedBits <= dataCapacityBits) break;
    }

    const dataCapacityBits = getNumDataCodewords(version, ecl) * 8;

    // Build the bit stream: mode indicator (0100 = byte) + char count + payload.
    const bb = [];
    const appendBits = (val, len) => {
        for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1);
    };
    appendBits(0x4, 4);
    appendBits(seg.numChars, charCountBits(version));
    for (const b of seg.bitData) bb.push(b);

    // Terminator + bit padding to a byte boundary + alternating byte padding.
    appendBits(0, Math.min(4, dataCapacityBits - bb.length));
    appendBits(0, (8 - (bb.length % 8)) % 8);
    for (let padByte = 0xec; bb.length < dataCapacityBits; padByte ^= 0xec ^ 0x11) {
        appendBits(padByte, 8);
    }

    // Pack bits into codeword bytes.
    const dataCodewords = new Array(bb.length / 8).fill(0);
    bb.forEach((bit, i) => { dataCodewords[i >>> 3] |= bit << (7 - (i & 7)); });

    return new QrCode(version, ecl, dataCodewords, -1);
}

export default { encodeText, Ecc };
